import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm, type Control } from 'react-hook-form';
import { z } from 'zod';

import { TextArea } from './TextArea.tsx';
import { TextField } from './TextField.tsx';
import {
  UpsertCounterpartyInputSchema,
  type Counterparty,
  type UpsertCounterpartyInput,
} from '../../shared/counterparty.ts';

type CounterpartyFormValues = z.input<typeof UpsertCounterpartyInputSchema>;
type CounterpartyFormControl = Control<CounterpartyFormValues, unknown, UpsertCounterpartyInput>;

export interface CustomerFormProps {
  customer?: Counterparty;
  submitLabel: string;
  submitting?: boolean;
  onCancel: () => void;
  onSubmit: (input: UpsertCounterpartyInput) => Promise<void>;
}

export function CustomerForm({
  customer,
  submitLabel,
  submitting,
  onCancel,
  onSubmit,
}: CustomerFormProps): React.ReactElement {
  const form = useForm<CounterpartyFormValues, unknown, UpsertCounterpartyInput>({
    resolver: zodResolver(UpsertCounterpartyInputSchema),
    defaultValues: toFormValues(customer),
  });

  return (
    <form
      noValidate
      className="min-w-0 max-w-full space-y-6 overflow-x-hidden"
      onSubmit={(event) => {
        void form.handleSubmit(onSubmit)(event);
      }}
    >
      <CustomerIdentityFields control={form.control} submitting={submitting} />
      <CustomerAddressFields control={form.control} submitting={submitting} />
      <CustomerFormActions submitLabel={submitLabel} submitting={submitting} onCancel={onCancel} />
    </form>
  );
}

function CustomerIdentityFields({
  control,
  submitting,
}: {
  control: CounterpartyFormControl;
  submitting?: boolean;
}): React.ReactElement {
  return (
    <div className="grid min-w-0 gap-5 md:grid-cols-2">
      <Controller
        name="name"
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            id="customer-name"
            label="Name"
            value={field.value ?? ''}
            errorText={fieldState.error?.message}
            disabled={submitting}
            onChange={field.onChange}
            onBlur={field.onBlur}
          />
        )}
      />
      <Controller
        name="email"
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            id="customer-email"
            label="Email"
            value={field.value ?? ''}
            errorText={fieldState.error?.message}
            disabled={submitting}
            onChange={field.onChange}
            onBlur={field.onBlur}
          />
        )}
      />
    </div>
  );
}

function CustomerAddressFields({
  control,
  submitting,
}: {
  control: CounterpartyFormControl;
  submitting?: boolean;
}): React.ReactElement {
  return (
    <section aria-label="Customer address fields" className="grid min-w-0 gap-5 lg:grid-cols-2">
      <Controller
        name="billTo"
        control={control}
        render={({ field, fieldState }) => (
          <TextArea
            id="customer-bill-to"
            label="Bill To"
            rows={6}
            value={field.value ?? ''}
            errorText={fieldState.error?.message}
            disabled={submitting}
            onChange={field.onChange}
            onBlur={field.onBlur}
          />
        )}
      />
      <Controller
        name="shipTo"
        control={control}
        render={({ field, fieldState }) => (
          <TextArea
            id="customer-ship-to"
            label="Ship To"
            rows={6}
            value={field.value ?? ''}
            errorText={fieldState.error?.message}
            disabled={submitting}
            onChange={field.onChange}
            onBlur={field.onBlur}
          />
        )}
      />
    </section>
  );
}

function CustomerFormActions({
  submitLabel,
  submitting,
  onCancel,
}: {
  submitLabel: string;
  submitting?: boolean;
  onCancel: () => void;
}): React.ReactElement {
  return (
    <div className="flex min-w-0 flex-col-reverse gap-3 sm:flex-row sm:justify-end">
      <button
        type="button"
        disabled={submitting}
        onClick={onCancel}
        className="inline-flex min-h-11 items-center justify-center rounded-full border border-line bg-paper px-4 py-2 text-sm font-medium text-ink transition hover:border-ochre/40 hover:bg-paper-deep disabled:cursor-not-allowed disabled:opacity-60"
      >
        Cancel
      </button>
      <button
        type="submit"
        disabled={submitting}
        className="inline-flex min-h-11 items-center justify-center rounded-full bg-ochre px-5 py-2 text-sm font-semibold text-paper-soft shadow-[0_8px_20px_-12px_rgba(176,119,43,0.85)] transition hover:bg-ochre-deep disabled:cursor-not-allowed disabled:opacity-60"
      >
        {submitting ? 'Saving…' : submitLabel}
      </button>
    </div>
  );
}

function toFormValues(customer?: Counterparty): CounterpartyFormValues {
  return {
    name: customer?.name ?? '',
    email: customer?.email ?? '',
    billTo: customer?.billTo ?? '',
    shipTo: customer?.shipTo ?? '',
  };
}
